/**
 * @file useEquipmentApi.js
 * @description API composable for equipment CRUD operations
 * Handles all HTTP requests related to equipment entities
 */

import { ref, toValue } from 'vue';
import { useQuasar } from 'quasar';
import brickSchemaService from '@/services/brickSchemaService';

/**
 * Composable for equipment API operations
 * @param {number|Ref<number>} siteId - The site ID (can be a ref or computed)
 * @returns {Object} Equipment API methods and state
 */
export function useEquipmentApi(siteId) {
  const $q = useQuasar();

  // State
  const equipmentList = ref([]);
  const loadingEquipment = ref(false);
  const saving = ref(false);

  /**
   * Load all equipment for the site
   * @param {boolean} bypassCache - Force fresh data from server
   * @returns {Promise<Array>} Equipment array
   */
  async function loadEquipmentList(bypassCache = false) {
    const unwrappedSiteId = toValue(siteId);
    if (!unwrappedSiteId) {
      return [];
    }

    try {
      loadingEquipment.value = true;

      const result = await brickSchemaService.equipment.getAll(unwrappedSiteId, bypassCache);

      if (!result.success) {
        throw new Error(result.error);
      }

      equipmentList.value = result.data.data || result.data || [];
      return equipmentList.value;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to load equipment: ${error.message}`,
      });
      return [];
    } finally {
      loadingEquipment.value = false;
    }
  }

  /**
   * Create new equipment
   * @param {Object} equipmentData - Equipment data to create
   * @returns {Promise<Object>} Created equipment
   */
  async function createEquipment(equipmentData) {
    try {
      saving.value = true;

      const result = await brickSchemaService.equipment.create(toValue(siteId), equipmentData);

      if (!result.success) {
        throw new Error(result.error);
      }

      const newEquipment = result.data.data || result.data;

      $q.notify({
        type: 'positive',
        message: `Equipment "${equipmentData.name}" created successfully`,
      });

      // Update local list
      equipmentList.value.push(newEquipment);

      return newEquipment;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to create equipment: ${error.message}`,
      });
      throw error;
    } finally {
      saving.value = false;
    }
  }

  /**
   * Update existing equipment
   * @param {number} equipmentId - Equipment ID
   * @param {Object} equipmentData - Updated equipment data
   * @returns {Promise<Object>} Updated equipment
   */
  async function updateEquipment(equipmentId, equipmentData) {
    try {
      saving.value = true;

      const result = await brickSchemaService.equipment.update(toValue(siteId), equipmentId, equipmentData);

      if (!result.success) {
        throw new Error(result.error);
      }

      const updatedEquipment = result.data.data || result.data;

      $q.notify({
        type: 'positive',
        message: 'Equipment updated successfully',
      });

      // Update local list
      const index = equipmentList.value.findIndex((eq) => eq.id === equipmentId);
      if (index > -1) {
        equipmentList.value[index] = updatedEquipment;
      }

      return updatedEquipment;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to update equipment: ${error.message}`,
      });
      throw error;
    } finally {
      saving.value = false;
    }
  }

  /**
   * Delete equipment
   * @param {number} equipmentId - Equipment ID to delete
   * @param {boolean} cascade - Delete children recursively
   * @returns {Promise<void>}
   */
  async function deleteEquipment(equipmentId, cascade = false) {
    try {
      const result = await brickSchemaService.equipment.delete(toValue(siteId), equipmentId, cascade);

      if (!result.success) {
        const error = new Error(result.error);
        // Preserve full response data for cascade dialog detection
        if (result.response) {
          error.response = result.response;
        }
        throw error;
      }

      // Remove from local list
      equipmentList.value = equipmentList.value.filter((eq) => eq.id !== equipmentId);
    } catch (error) {
      // Re-throw with response data intact - don't show notification here
      throw error;
    }
  }

  /**
   * Load equipment for a specific space
   * @param {number} spaceId - Space ID
   * @returns {Promise<Array>} Equipment in the space
   */
  async function loadSpaceEquipment(spaceId) {
    try {
      const result = await brickSchemaService.equipment.getBySpace(toValue(siteId), spaceId);

      if (!result.success) {
        throw new Error(result.error);
      }

      return result.data.data || result.data || [];
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to load space equipment: ${error.message}`,
      });
      return [];
    }
  }

  /**
   * Load equipment feeds relationships
   * @param {number} equipmentId - Equipment ID
   * @returns {Promise<Object|null>} Feeds relationship data
   */
  async function loadEquipmentFeedsRelationship(equipmentId) {
    try {
      const result = await brickSchemaService.equipment.getRelationships(toValue(siteId), equipmentId);

      if (!result.success) {
        return null;
      }

      return result.data || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Duplicate equipment
   * @param {number} equipmentId - Equipment ID to duplicate
   * @param {string} newName - Name for the duplicated equipment
   * @returns {Promise<Object>} Duplicated equipment
   */
  async function duplicateEquipment(equipmentId, newName) {
    try {
      saving.value = true;

      const result = await brickSchemaService.equipment.duplicate(toValue(siteId), equipmentId, newName);

      if (!result.success) {
        throw new Error(result.error);
      }

      const duplicatedEquipment = result.data.data || result.data;

      $q.notify({
        type: 'positive',
        message: `Equipment duplicated as "${newName}"`,
      });

      // Add to local list
      equipmentList.value.push(duplicatedEquipment);

      return duplicatedEquipment;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to duplicate equipment: ${error.message}`,
      });
      throw error;
    } finally {
      saving.value = false;
    }
  }

  return {
    // State
    equipmentList,
    loadingEquipment,
    saving,

    // Methods
    loadEquipmentList,
    createEquipment,
    updateEquipment,
    deleteEquipment,
    loadSpaceEquipment,
    loadEquipmentFeedsRelationship,
    duplicateEquipment,
  };
}
